/**@author Hieu Manh */
import React, { useMemo } from "react";
import { KeyboardTypeOptions, Platform, StyleSheet, Text, TextInput, View } from "react-native";
import { useTheme } from "react-native-paper";

interface KeycodeInputProps {
  maxLength: number;
  tintColor?: string;
  textColor?: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  autoFocus?: boolean;
  uppercase?: boolean;
  alphaNumeric?: boolean;
  numeric?: boolean;
  value: string;
  style?: any;
  isFocus?: boolean;
}

export const InputPinCode = (props: KeycodeInputProps) => {
  const {
    maxLength,
    tintColor,
    textColor,
    onChange = () => {},
    onComplete = () => {},
    autoFocus,
    uppercase,
    alphaNumeric,
    numeric,
    value,
    style,
    isFocus = true,
  } = props;
  const { colors, fonts } = useTheme();

  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          flexDirection: "row",
          alignItems: "center",
          position: "relative",
        },
        input: {
          height: 48,
          position: "absolute",
          opacity: 0,
          zIndex: 100,
        },
        box: {
          width: 32,
          marginHorizontal: 5,
        },
        bar: {
          backgroundColor: colors.divider,
          height: 1,
          width: 32,
        },
        barActive: {
          height: 2,
          marginTop: -0.5,
        },
        text: {
          fontSize: 32,
          lineHeight: 36,
          height: 36,
          textAlign: "center",
          width: 32,
          marginBottom: 8,
          ...fonts.semibold,
        },
      }),
    [fonts, colors],
  );

  const [inputValue, setInputValue] = React.useState<string>(value);

  const txtInputRef = React.useRef<TextInput>(null);

  React.useEffect(() => {
    if (value !== undefined && value !== inputValue) {
      setInputValue(value);
    }
  }, [value, inputValue]);

  if (value !== undefined && !onChange) {
    throw new Error("To use the KeycodeInput as a controlled component, " + "you need to supply both the value and onChange props.");
  }

  const changeText = (_value: string) => {
    _value = _value.replace(" ", "");
    if (uppercase) {
      _value = _value.toUpperCase();
    }
    if (alphaNumeric) {
      _value = _value.replace("/[^a-z0-9]/i", "");
    }
    setInputValue(_value);
    onChange(_value);
    if (_value.length < maxLength) {
      return;
    }
    onComplete(_value);
  };

  const renderBoxes = () => {
    let elements = [];
    let i = 0;
    let vals = inputValue.split("");
    while (i < maxLength) {
      let active = i === inputValue.length;
      let barStyles = [styles.bar, active ? [styles.barActive, { backgroundColor: tintColor ? tintColor : colors.accent }] : []];

      elements.push(
        <View style={styles.box} key={i}>
          <Text style={styles.text}>{vals[i] || ""}</Text>
          <View style={barStyles} />
        </View>,
      );

      i++;
    }

    return elements;
  };

  const focusInput = React.useCallback(() => {
    txtInputRef.current?.focus();
  }, []);

  let keyboardType: KeyboardTypeOptions = numeric ? "numeric" : Platform.OS === "ios" ? "ascii-capable" : "default";

  React.useEffect(() => {
    if (isFocus) focusInput();
  }, [focusInput, isFocus]);

  return (
    <View style={[styles.container, style]}>
      {renderBoxes()}
      <TextInput
        ref={txtInputRef}
        style={[styles.input, { color: textColor ? textColor : colors.blackText, width: 42 * maxLength }]}
        autoFocus={autoFocus}
        autoCorrect={false}
        autoCapitalize="characters"
        value={inputValue}
        blurOnSubmit={false}
        keyboardType={keyboardType}
        maxLength={maxLength}
        disableFullscreenUI
        clearButtonMode="never"
        spellCheck={false}
        returnKeyType="go"
        underlineColorAndroid="transparent"
        onChangeText={(text) => changeText(text)}
        caretHidden
      />
    </View>
  );
};
